import { RequestsStore, resetRequests, RequestAction } from '@redux-requests/core';
import authService from 'modules/auth/authService';
import { initAuthAction, refreshTokenAction, logoutAction } from 'modules/auth/store/actions';

export const onRequest = (
  request: any,
  requestAction: RequestAction,
  store: RequestsStore
): any => {
  const expiredIn = authService.getExpiredIn() ?? 0;
  const accessToken = authService.getAccessToken();
  const isNeverLoggedIn = expiredIn === 0 && accessToken === null; // for reset password to work

  // checks access token expiration on each request
  // because server don't respond with error when access token is expired
  if ((Date.now() > expiredIn || !accessToken) && !isNeverLoggedIn) {
    const refreshToken = authService.getRefreshToken();

    if (!refreshToken) {
      // cleans requests store
      return store.dispatch(logoutAction() as any);
    }

    store
      .dispatch(initAuthAction(refreshToken))
      .then(async () => await store.dispatchRequest(requestAction));
  }

  const headers = { ...request.headers };

  if (!headers.Authorization && accessToken) {
    headers.Authorization = accessToken;
  }

  return {
    ...request,
    headers,
  };
};

// TODO: Add onSuccess empty response handler

export const onError = async (
  error: any,
  requestAction: any,
  store: RequestsStore
): Promise<any> => {
  // Error status with expired access token must be 401
  if (error.response?.status === 401) {
    const localRefreshToken = authService.getRefreshToken();

    if (!localRefreshToken) {
      await store.dispatch(resetRequests());
      authService.clearAuthTokens();
      throw error;
    }

    const { data } = await store.dispatchRequest(refreshTokenAction(localRefreshToken));

    // didn't manage to get new token
    if (!data) {
      await store.dispatch(resetRequests());
      authService.clearAuthTokens();
      throw error;
    }

    const {
      refreshTokens: { authorization, refreshToken },
    } = data;

    // save refresh and access token
    authService.setRefreshToken(authorization);
    authService.setAccessToken(refreshToken);

    // fire the same request again with new token
    const newResponse = await store.dispatchRequest({
      ...requestAction,
      request: {
        ...requestAction.request,
        headers: {
          ...requestAction.request.headers,
          Authorization: authorization,
        },
      },
      meta: {
        ...requestAction.meta,
        silent: true, // to avoid duplicated request and response actions
        runOnError: false, // to prevent potential infinite loops!
        runOnSuccess: false, // to prevent double run of onSuccess for this action
      },
    });

    if (newResponse.data) {
      return { data: newResponse.data };
    }
  }

  // either not token related error or failed again
  throw error;
};
